import { useEffect, useState } from 'react';
import type { SessionOutcome } from '@bettercomp/ams-spa-auth';
import { workspaceCodeOf } from 'service/amsToken';
import { onTenancySwitch } from 'service/tenancyBroadcast';
import { reportAction } from 'utils/reporting';

type MoveTrigger = 'token' | 'broadcast';

/**
 * Keeps the page in the workspace of its first token. A later token for another workspace, or a switch
 * in another tab, returns true: the gate swaps the page for its spinner, then the tab reloads.
 */
export function usePageWorkspace(sessionOutcome: SessionOutcome | undefined): boolean {
  const [pageWorkspace, setPageWorkspace] = useState<string>();
  // Set once: the first trigger wins, so the tab reloads and reports once.
  const [movedBy, setMovedBy] = useState<MoveTrigger>();
  const tokenWorkspace = sessionOutcome?.kind === 'minted' ? workspaceCodeOf(sessionOutcome.accessToken) : undefined;

  useEffect(() => {
    if (!tokenWorkspace) {
      return;
    }
    if (!pageWorkspace) {
      setPageWorkspace(tokenWorkspace);
    } else if (tokenWorkspace !== pageWorkspace) {
      setMovedBy((trigger) => trigger ?? 'token');
    }
  }, [tokenWorkspace, pageWorkspace]);

  // Only once the page has a workspace: before its first mint there is nothing to leave.
  useEffect(() => {
    if (!pageWorkspace) {
      return undefined;
    }
    return onTenancySwitch(() => setMovedBy((trigger) => trigger ?? 'broadcast'));
  }, [pageWorkspace]);

  // Runs after the commit that unmounted the page, so its unsaved-changes guard cannot block the reload.
  useEffect(() => {
    if (!movedBy) {
      return;
    }
    reportAction('ams_workspace_moved', { trigger: movedBy });
    window.location.reload();
  }, [movedBy]);

  return movedBy !== undefined;
}
